// useSchedule.ts
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { CarouselRef } from 'antd/es/carousel'
import { format, addDays, startOfWeek, getHours, parse } from 'date-fns'
import { accumulateDayScheduleCounts, type DayScheduleCounts } from './scheduleCountHelpers'
import { getScheduleByWeek, getScheduleAvailability, resetSchedule } from '../../../redux/flows/portal/studio'
import { useAppDispatch, useAppSelector } from '../../../redux/store'
import { APIStatus } from '../../../redux/types'

// ------------ Types ------------
type ScheduleItem = {
  appointmentTime: string
  isMember: boolean
  needsFullSlot: boolean
  isScanBooking: boolean
  count: number
}

export const useSchedule = () => {
  const dispatch = useAppDispatch()
  const clinicId = useAppSelector((state) => state.studioAuth.studioDetails?.id)
  const { schedule, scheduleStatus, scheduleAvailability, scheduleAvailabilityStatus } = useAppSelector(
    (state) => state.studioPortal,
  )

  const TOTAL_WEEKS = 2
  const [centralWeekIndex, setCentralWeekIndex] = useState(0)
  const [weekStarts, setWeekStarts] = useState<Date[]>([])
  const [loading, setLoading] = useState(true)
  const [calendarData, setCalendarData] = useState<any>({})
  const carouselRef = useRef<CarouselRef>(null)

  // ---------------- LIVE TIME ----------------
  const [currentTime, setCurrentTime] = useState(new Date())

  useEffect(() => {
    const interval = setInterval(() => setCurrentTime(new Date()), 60000)
    return () => clearInterval(interval)
  }, [])

  const getCurrentTimeOffset = useCallback(() => {
    const hour = currentTime.getHours()
    const minutes = currentTime.getMinutes()

    if (hour < 8 || hour >= 20) return null

    const clampedMinutes = Math.max(15, Math.min(minutes, 45))
    const minutesSinceStart = (hour - 8) * 60 + clampedMinutes
    const rowHeight = 64

    return (minutesSinceStart / 60) * rowHeight
  }, [currentTime])

  // ---------------- Date Helpers ----------------
  const getCurrentWeekMonday = useCallback(() => {
    return startOfWeek(new Date(), { weekStartsOn: 1 })
  }, [])

  const generateDays = useCallback((weekStart: Date) => {
    const days = []
    for (let i = 0; i < 7; i++) {
      const d = addDays(weekStart, i)
      days.push({
        date: d,
        dayName: `${format(d, 'EEEE').toUpperCase()} ${format(d, 'd')}`,
        dayNumber: format(d, 'd'),
      })
    }
    return days
  }, [])

  const headerDays = weekStarts.length > 0 ? generateDays(weekStarts[centralWeekIndex]) : []

  // ---------------- Calculate day header counts ----------------
  const dayCounts = useMemo(() => {
    if (!schedule || schedule.length === 0 || weekStarts.length === 0) {
      return {} as Record<string, DayScheduleCounts>
    }

    const weekStart = weekStarts[centralWeekIndex]
    const counts: Record<string, DayScheduleCounts> = {}
    const weekStartStr = format(weekStart, 'yyyy-MM-dd')
    const weekEndStr = format(addDays(weekStart, 6), 'yyyy-MM-dd')

    schedule.forEach((item) => {
      const appointmentDate = new Date(item.appointmentTime)
      const appointmentDateStr = format(appointmentDate, 'yyyy-MM-dd')

      if (appointmentDateStr >= weekStartStr && appointmentDateStr <= weekEndStr) {
        const dayKey = format(appointmentDate, 'EEE').toUpperCase()
        accumulateDayScheduleCounts(counts, dayKey, item, appointmentDate)
      }
    })

    return counts
  }, [schedule, weekStarts, centralWeekIndex])

  // ---------------- Calculate Total Appointments for Current Week ----------------
  const totalWeekAppointments = useMemo(() => {
    if (!schedule || schedule.length === 0 || weekStarts.length === 0) {
      return 0
    }

    const weekStart = weekStarts[centralWeekIndex]
    const weekStartStr = format(weekStart, 'yyyy-MM-dd')
    const weekEndStr = format(addDays(weekStart, 6), 'yyyy-MM-dd')

    return schedule.reduce((total, item) => {
      const appointmentDate = new Date(item.appointmentTime)
      const appointmentDateStr = format(appointmentDate, 'yyyy-MM-dd')

      if (appointmentDateStr >= weekStartStr && appointmentDateStr <= weekEndStr) {
        return total + (Number(item.count) || 1)
      }

      return total
    }, 0)
  }, [schedule, weekStarts, centralWeekIndex])

  // ---------------- Blocked (unbookable) slots ----------------
  // Quarter-hours with no schedule window set — the "anti-schedule". Times are
  // clinic wall-clock, same as the rendered grid.
  const blockedData = useMemo(() => {
    if (scheduleAvailabilityStatus !== APIStatus.fulfilled || weekStarts.length === 0) {
      return {} as Record<string, Record<string, Record<number, boolean[]>>>
    }

    const weekStart = weekStarts[centralWeekIndex]
    const weekStartStr = format(weekStart, 'yyyy-MM-dd')
    const weekEndStr = format(addDays(weekStart, 6), 'yyyy-MM-dd')

    const toMinutes = (time: string) => {
      const [h, m] = time.split(':')
      return Number(h) * 60 + Number(m)
    }

    const byDay: Record<string, Record<number, boolean[]>> = {}

    scheduleAvailability.forEach((dayAvailability) => {
      if (dayAvailability.date < weekStartStr || dayAvailability.date > weekEndStr) return

      const dayLabel = format(parse(dayAvailability.date, 'yyyy-MM-dd', new Date()), 'EEE').toUpperCase()
      const bookableRanges = dayAvailability.windows.map(
        (w) => [toMinutes(w.startTime), toMinutes(w.endTime)] as [number, number],
      )

      const hours: Record<number, boolean[]> = {}
      for (let hour = 8; hour <= 20; hour++) {
        const quarters = [0, 1, 2, 3].map((q) => {
          const minutes = hour * 60 + q * 15
          return !bookableRanges.some(([start, end]) => minutes >= start && minutes < end)
        })
        if (quarters.some(Boolean)) hours[hour] = quarters
      }
      byDay[dayLabel] = hours
    })

    return { [weekStartStr]: byDay }
  }, [scheduleAvailability, scheduleAvailabilityStatus, weekStarts, centralWeekIndex])

  // ---------------- Color & Opacity ----------------
  const assignColor = useCallback((isMember: boolean, needsFull: boolean, isScanBooking: boolean) => {
    if (!isMember && needsFull) return '#007f7c'
    else if (isMember && needsFull && isScanBooking) return '#000000'
    else if (isMember) return '#FFFAFA'
    return '#007f7c'
  }, [])

  // const assignColor = useCallback((isMember: boolean, needsFull: boolean) => {
  //   if (!isMember && needsFull) return '#e5e7eb'
  //   return '#007f7c'
  // }, [])

  // const assignOpacity = useCallback((c: number, isMember: boolean, needsFull: boolean) => {
  //   if ((isMember && needsFull) || (!isMember && needsFull)) return 1
  //   if (c === 1) return 1
  //   if (c === 2) return 0.75
  //   return 0.5
  // }, [])

  // ---------------- Transform UTC to EST ----------------
  const convertUtcToEst = useCallback((utcString: string) => {
    const d = new Date(utcString)
    return d.toLocaleString('en-US', {
      timeZone: 'America/New_York',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    })
  }, [])

  // ---------------- Main Transform ----------------
  const transformDataForCalendar = useCallback(
    (data: ScheduleItem[]) => {
      return data.reduce((acc, item) => {
        const date = new Date(convertUtcToEst(item.appointmentTime))
        const dayLabel = format(date, 'EEE').toUpperCase()
        const hour = getHours(date)
        const minutes = date.getMinutes()
        const quarter = Math.floor(minutes / 15)

        if (hour >= 8 && hour < 20) {
          if (!acc[dayLabel]) acc[dayLabel] = {}
          if (!acc[dayLabel][hour]) acc[dayLabel][hour] = [[], [], [], []]

          // Check if appointment is in the past
          const isPast = date < currentTime
          const opacity = isPast ? 0.3 : 1

          acc[dayLabel][hour][quarter].push({
            id: item.appointmentTime,
            color: assignColor(item.isMember, item.needsFullSlot, item.isScanBooking),
            opacity,
            outlined: item.needsFullSlot && item.isMember && !item.isScanBooking,
            count: item.count,
            isMember: item.isMember,
            date: format(date, 'yyyy-MM-dd'),
          })
        }

        return acc
      }, {} as any)
    },
    [assignColor, convertUtcToEst, currentTime],
  )

  // ---------------- Week Start Setup ----------------
  useEffect(() => {
    const start = getCurrentWeekMonday()
    const arr: Date[] = []
    for (let i = 0; i < TOTAL_WEEKS; i++) arr.push(addDays(start, i * 7))
    setWeekStarts(arr)
  }, [getCurrentWeekMonday])

  // ---------------- Fetch from API ----------------
  useEffect(() => {
    if (weekStarts.length === 0 || !clinicId) return

    const weekStart = weekStarts[centralWeekIndex]
    const startDate = format(weekStart, 'yyyy-MM-dd')
    const endDate = format(addDays(weekStart, 6), 'yyyy-MM-dd')

    setLoading(true)
    dispatch(
      getScheduleByWeek({
        startDate,
        endDate,
        clinicId,
      }) as any,
    )
    dispatch(
      getScheduleAvailability({
        startDate,
        endDate,
        clinicId,
      }) as any,
    )
  }, [centralWeekIndex, weekStarts, clinicId, dispatch])

  // ---------------- Transform API data into calendar ----------------
  useEffect(() => {
    if (scheduleStatus !== APIStatus.fulfilled) return
    if (weekStarts.length === 0) return

    const weekStart = weekStarts[centralWeekIndex]
    const formattedDate = format(weekStart, 'yyyy-MM-dd')

    const events: ScheduleItem[] =
      schedule?.map((item) => ({
        appointmentTime: item.appointmentTime,
        isMember: item.isMember,
        needsFullSlot: item.needsFullSlot,
        isScanBooking: Boolean(item.isScanBooking),
        count: Number(item.count),
      })) || []

    const calendarChunk = transformDataForCalendar(events)
    setCalendarData((p: any) => ({ ...p, [formattedDate]: calendarChunk }))
    setLoading(false)
  }, [schedule, scheduleStatus, centralWeekIndex, weekStarts, transformDataForCalendar])

  // ---------------- Carousel Callback ----------------
  const handleCarouselChange = useCallback(
    (next: number) => {
      if (next === centralWeekIndex) return
      dispatch(resetSchedule())
      setLoading(true)
      setCalendarData({})
      setCentralWeekIndex(next)
    },
    [centralWeekIndex],
  )

  useEffect(() => {
    return () => {
      dispatch(resetSchedule())
    }
  }, [])

  // ------------ Return Values ------------
  return {
    // state
    loading,
    weekStarts,
    headerDays,
    calendarData,
    blockedData,
    centralWeekIndex,
    currentTime,
    dayCounts,
    totalWeekAppointments,

    // refs
    carouselRef,

    // helpers
    getCurrentTimeOffset,
    handleCarouselChange,
  }
}
